import Image from "next/image";

/**
 * Convertit un code de langue en emoji de drapeau
 * @param {string} languageCode - Code de langue (ex: 'fr', 'en', 'es')
 * @returns {string} - Emoji du drapeau correspondant
 */
export const getFlagEmoji = (languageCode) => {
    if (!languageCode || typeof languageCode !== "string") {
        return "🌐"; // Globe par défaut
    }

    // Mapping des codes de langue vers les emojis de drapeaux
    const flagMap = {
        // Langues principales
        fr: (
            <Image
                src="/static/flags/fr.svg"
                alt="French"
                width={24}
                height={24}
            />
        ), // Français
        en: (
            <Image
                src="/static/flags/en.svg"
                alt="English"
                width={24}
                height={24}
            />
        ), // Anglais (USA)
        es: "🇪🇸", // Espagnol
        de: "🇩🇪", // Allemand
        it: "🇮🇹", // Italien
        pt: "🇵🇹", // Portugais
        ru: "🇷🇺", // Russe
        zh: "🇨🇳", // Chinois
        ja: "🇯🇵", // Japonais
        ko: "🇰🇷", // Coréen
        ar: "🇸🇦", // Arabe
        hi: "🇮🇳", // Hindi
        nl: "🇳🇱", // Néerlandais
        sv: "🇸🇪", // Suédois
        no: "🇳🇴", // Norvégien
        da: "🇩🇰", // Danois
        fi: "🇫🇮", // Finnois
        pl: "🇵🇱", // Polonais
        tr: "🇹🇷", // Turc
        he: "🇮🇱", // Hébreu
        th: "🇹🇭", // Thaï
        vi: "🇻🇳", // Vietnamien
        uk: "🇺🇦", // Ukrainien
        cs: "🇨🇿", // Tchèque
        sk: "🇸🇰", // Slovaque
        hu: "🇭🇺", // Hongrois
        ro: "🇷🇴", // Roumain
        bg: "🇧🇬", // Bulgare
        hr: "🇭🇷", // Croate
        sl: "🇸🇮", // Slovène
        et: "🇪🇪", // Estonien
        lv: "🇱🇻", // Letton
        lt: "🇱🇹", // Lituanien
        mt: "🇲🇹", // Maltais
        ga: "🇮🇪", // Irlandais
        cy: "🏴󠁧󠁢󠁷󠁬󠁳󠁿", // Gallois
        is: "🇮🇸", // Islandais
        id: "🇮🇩", // Indonésien
        ms: "🇲🇾", // Malais
        tl: "🇵🇭", // Tagalog
        sw: "🇰🇪", // Swahili
        af: "🇿🇦", // Afrikaans
        zu: "🇿🇦", // Zoulou
        xh: "🇿🇦", // Xhosa
        am: "🇪🇹", // Amharique
        fa: "🇮🇷", // Persan
        ur: "🇵🇰", // Ourdou
        bn: "🇧🇩", // Bengali
        gu: "🇮🇳", // Gujarati
        ta: "🇮🇳", // Tamoul
        te: "🇮🇳", // Télougou
        kn: "🇮🇳", // Kannada
        ml: "🇮🇳", // Malayalam
        si: "🇱🇰", // Cinghalais
        my: "🇲🇲", // Birman
        km: "🇰🇭", // Khmer
        lo: "🇱🇦", // Lao
        ka: "🇬🇪", // Géorgien
        hy: "🇦🇲", // Arménien
        az: "🇦🇿", // Azéri
        kk: "🇰🇿", // Kazakh
        ky: "🇰🇬", // Kirghize
        uz: "🇺🇿", // Ouzbek
        tj: "🇹🇯", // Tadjik
        mn: "🇲🇳", // Mongol
        ne: "🇳🇵", // Népalais
        dz: "🇧🇹", // Dzongkha

        // Variantes régionales communes
        "en-gb": "🇬🇧", // Anglais britannique
        "en-au": "🇦🇺", // Anglais australien
        "en-ca": "🇨🇦", // Anglais canadien
        "fr-ca": "🇨🇦", // Français canadien
        "fr-ch": "🇨🇭", // Français suisse
        "de-ch": "🇨🇭", // Allemand suisse
        "de-at": "🇦🇹", // Allemand autrichien
        "es-mx": "🇲🇽", // Espagnol mexicain
        "es-ar": "🇦🇷", // Espagnol argentin
        "pt-br": "🇧🇷", // Portugais brésilien
        "zh-tw": "🇹🇼", // Chinois traditionnel (Taiwan)
        "zh-hk": "🇭🇰", // Chinois (Hong Kong)
    };

    // Normaliser le code de langue en minuscules
    const normalizedCode = languageCode.toLowerCase().trim();

    // Retourner le drapeau correspondant ou un globe par défaut
    return flagMap[normalizedCode] || "🌐";
};

/**
 * Convertit un code de langue en emoji de drapeau avec un fallback personnalisé
 * @param {string} languageCode - Code de langue
 * @param {string} fallback - Emoji de fallback (défaut: 🌐)
 * @returns {string} - Emoji du drapeau correspondant
 */
export const getFlagEmojiWithFallback = (languageCode, fallback = "🌐") => {
    const flag = getFlagEmoji(languageCode);
    return flag === "🌐" ? fallback : flag;
};

/**
 * Vérifie si un code de langue a un drapeau emoji disponible
 * @param {string} languageCode - Code de langue
 * @returns {boolean} - True si un drapeau est disponible
 */
export const hasFlagEmoji = (languageCode) => {
    return getFlagEmoji(languageCode) !== "🌐";
};
